/**
 * UnmappedOutletsTab — Phase R10.4c (2026-07-05)
 *
 * Lists aggregated unmapped detected outlets for the owner's brand.
 * Each row: original name + normalized hint + receipt count + latest
 * detection date + "Save Mapping" CTA. Clicking Save opens
 * <AssignBranchModal mode="create" />.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Store, Clock, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { SectionCard, QueryError } from '@/components/ui_primitives/Misc';
import AssignBranchModal from './AssignBranchModal';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('id-ID', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function UnmappedOutletsTab() {
  const token = useAuthStore((s) => s.token);
  const qc    = useQueryClient();
  const [modalRow, setModalRow] = useState(null);

  const unmappedQ = useQuery({
    queryKey: ['owner-outlet-mappings-unmapped'],
    queryFn:  () => api.ownerListUnmappedOutlets(token),
    enabled:  !!token,
    staleTime: 30_000,
  });

  const branchesQ = useQuery({
    queryKey: ['owner-branches-for-mapping'],
    queryFn:  () => api.ownerListBranches(token),
    enabled:  !!token,
    staleTime: 60_000,
  });

  const createMut = useMutation({
    mutationFn: ({ originalDetectedOutlet, mappedBranchId, applyToPending }) =>
      api.ownerCreateOutletMapping(token, {
        originalDetectedOutlet, mappedBranchId, applyToPending,
      }),
    onSuccess: (res) => {
      const applied = res?.applied_to_claims_count ?? 0;
      toast.success(
        applied > 0
          ? `Mapping saved — ${applied} pending receipt${applied === 1 ? '' : 's'} tagged.`
          : 'Mapping saved.',
      );
      setModalRow(null);
      qc.invalidateQueries({ queryKey: ['owner-outlet-mappings-unmapped'] });
      qc.invalidateQueries({ queryKey: ['owner-outlet-mappings-list'] });
    },
    onError: (e) => {
      toast.error(e?.detail?.message ?? e?.detail ?? 'Could not save the mapping');
    },
  });

  const rows = useMemo(
    () => (unmappedQ.data?.items ?? []),
    [unmappedQ.data],
  );
  const branches = Array.isArray(branchesQ.data) ? branchesQ.data : [];

  if (unmappedQ.isLoading) {
    return (
      <SectionCard>
        <div className="py-10 flex items-center justify-center gap-2 text-white/55 text-sm">
          <Loader2 size={16} className="animate-spin text-gold" />
          Loading unmapped outlets…
        </div>
      </SectionCard>
    );
  }
  if (unmappedQ.isError) {
    return <QueryError error={unmappedQ.error} onRetry={unmappedQ.refetch} />;
  }

  return (
    <SectionCard>
      <div
        className="flex items-start justify-between gap-3 mb-4"
        data-testid="outlet-mapping-unmapped-header"
      >
        <div>
          <h3 className="text-lg font-display font-semibold text-white inline-flex items-center gap-2">
            <Store size={16} className="text-gold" />
            Unmapped Outlets
          </h3>
          <p className="text-sm text-white/55 mt-1">
            Detected outlets with pending receipts that have no branch yet.
            Save a mapping to assign the branch — future receipts auto-resolve.
          </p>
        </div>
        <div
          className="text-xs text-white/50"
          data-testid="outlet-mapping-unmapped-total"
        >
          {rows.length} outlet{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-white/50 border border-dashed border-white/10 rounded-2xl">
          No unmapped outlets right now — every pending receipt already has a branch.
        </div>
      ) : (
        <div className="space-y-2" data-testid="outlet-mapping-unmapped-rows">
          {rows.map((row) => (
            <div
              key={row.normalized_detected_outlet}
              className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid={`outlet-mapping-unmapped-row-${row.normalized_detected_outlet}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">
                  {row.original_detected_outlet}
                </div>
                <div className="text-[11px] text-white/40 font-mono truncate">
                  normalized: {row.normalized_detected_outlet}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-white/60 whitespace-nowrap">
                <span className="inline-flex items-center gap-1" data-testid={`outlet-mapping-count-${row.normalized_detected_outlet}`}>
                  <Receipt size={12} />
                  {row.receipt_count}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} />
                  {fmtDate(row.latest_detection_date)}
                </span>
                <button
                  type="button"
                  onClick={() => setModalRow(row)}
                  className="ml-2 inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold rounded-xl px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={branchesQ.isLoading || createMut.isPending}
                  data-testid={`outlet-mapping-save-${row.normalized_detected_outlet}`}
                >
                  Save Mapping
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalRow ? (
        <AssignBranchModal
          mode="create"
          initialOutlet={modalRow.original_detected_outlet}
          normalizedOutlet={modalRow.normalized_detected_outlet}
          receiptCount={modalRow.receipt_count}
          branches={branches}
          loadingBranches={branchesQ.isLoading}
          pending={createMut.isPending}
          onClose={() => setModalRow(null)}
          onSubmit={({ mappedBranchId, applyToPending }) => {
            createMut.mutate({
              originalDetectedOutlet: modalRow.original_detected_outlet,
              mappedBranchId,
              applyToPending,
            });
          }}
        />
      ) : null}
    </SectionCard>
  );
}
